import * as React from 'react';
import { createFragmentContainer, graphql, QueryRenderer } from 'react-relay';
import { Environment } from 'relay-runtime';

import Cookie from 'js-cookie';
import getMeta from './utils/getMeta';
import { shopWithCeo } from './utils/shopWithCeo';
import { supercampaignConfig } from '../../routes/supercampaignConfig';
import { trackPageView } from '../../utils/tracking';
import { AppSideEffects_user } from './__generated__/AppSideEffects_user.graphql';
import { handleArchivedUser } from './utils/handleArchivedUser';

type AppSideEffectsProps = {
  // const { query } = this.props.match.location;
  query: any | null;
  user: AppSideEffects_user;
};

// Handles Side Effects
const AppSideEffects = ({
  user,
  query,
  relay: { environment },
}: AppSideEffectsProps) => {
  const userId = user && user.userId;
  // Log user out if they are an archived account
  React.useEffect(() => {
    if (userId) {
      handleArchivedUser(environment, userId);
    }
  }, [userId, environment]);
  // Segment: Track your page
  const { pathname, href } = window.location;
  const permissions = user && user.permissions;
  React.useEffect(() => {
    const hasViaTrackingCode = href.includes('?via=');
    if (hasViaTrackingCode) {
      const referralCode = new RegExp('[\\?&]via=([^&#]*)').exec(url)[1];
      Cookie.set('OHReferralCode', referralCode, { expires: 90 });
    }
  }, []);
  React.useEffect(() => {
    if (!href.includes('order')) {
      setTimeout(() => {
        const seoTitle = getMeta('title');
        trackPageView(
          user,
          seoTitle,
          {
            url: href,
            path: pathname,
          },
          permissions,
        );
      }, 1000);
    }
  }, [pathname, href, getMeta, trackPageView, permissions]);
  const isCe =
    permissions &&
    permissions.oneCommerce &&
    permissions.oneCommerce.ceo &&
    permissions.oneCommerce.ceo.access;
  React.useEffect(() => {
    if (isCe) {
      if (!Cookie.get('hasCePermissions')) {
        Cookie.set('hasCePermissions', 'true', {
          domain:
            process.env.NODE_ENV !== 'production'
              ? 'localhost'
              : 'onehopewine.com',
        });
      }
    }
  }, [isCe]);
  /**
   * Allow referrals either from query params or localStorage
   *
   * localStorage makes it easy for other apps on our domain - e.g. the blog - to set referrals
   * without worrying about rewriting links to modify the query string
   */
  React.useEffect(() => {
    const ceo = query ? query.ceo : null;
    const dealer = query ? query.dealer : null;
    const event = query ? query.event : null;

    let [lsCeo, lsDealer, lsEvent] = ['', '', ''];
    if (window && window.localStorage) {
      lsCeo = window.localStorage.getItem('ceo');
      lsDealer = window.localStorage.getItem('dealer');
      lsEvent = window.localStorage.getItem('event');
    }

    // query params take precedence as they are probably more relevant
    // e.g. someone got referred to the blog a week ago but used a referral link on this request
    if (ceo || dealer || event) {
      const isSuperCampaign = supercampaignConfig.find((slug: string) => {
        return `/${slug}` === pathname;
      });
      const isGuest = user && user.userId && user.userId.includes('.');
      if (isSuperCampaign && isGuest) {
        shopWithCeo({
          ceoCustomUrl: ceo,
          trinityDealerId: dealer,
          eventId: event,
          environment,
          preventEventExit: true,
        });
      } else {
        shopWithCeo({
          ceoCustomUrl: ceo,
          trinityDealerId: dealer,
          eventId: event,
          environment,
        });
      }
    } else if (lsCeo || lsDealer || lsEvent) {
      shopWithCeo({
        ceoCustomUrl: lsCeo,
        trinityDealerId: lsDealer,
        eventId: lsEvent,
        environment,
      });

      // consume localStorage values so we don't run this on every page load
      window.localStorage.removeItem('ceo');
      window.localStorage.removeItem('dealer');
      window.localStorage.removeItem('event');
    }
  }, []);

  // smartlook
  React.useEffect(() => {
    if (!window) {
      return;
    }
    const userIdentifierSmartlook = user.email || user.userId;
    if (!userIdentifierSmartlook) {
      return;
    }
    try {
      // @ts-ignore
      if (window.smartlook) {
        // @ts-ignore
        window.smartlook('identify', userIdentifierSmartlook);
      }
    } catch (err) {
      console.warn('Failed to ID user with Smartlook:', err);
    }
  }, [typeof window && window, user.email, user.userId]);

  // create cookie with user role for ProdPerfect to consume via Google Tag Manager
  React.useEffect(() => {
    /**
     * Determine the user's highest level of privilege. ProdPerfect uses this to segment users based on what they have
     * access to, so we care about what they're able to do but not about if they are a CE and also a Subscriber, for
     * example.
     */
    const getRoleIdentifier = (
      userPermissions: AppSideEffects_user['permissions'] | null,
    ): string => {
      if (!userPermissions || !userPermissions.oneCommerce) {
        return 'retail';
      }

      const { oneCommerce } = userPermissions;

      switch (true) {
        default:
          return 'retail';

        case oneCommerce.admin && oneCommerce.admin.access:
        case oneCommerce.superAdmin && oneCommerce.superAdmin.access:
          return 'admin';

        case oneCommerce.ceo && oneCommerce.ceo.access:
          return 'ce';

        case oneCommerce.subscriber && oneCommerce.subscriber.access:
          return 'subscriber';

        case oneCommerce.preferredCustomer &&
          oneCommerce.preferredCustomer.access:
          return 'pc';
      }
    };

    Cookie.set('PPUserRole', getRoleIdentifier(user.permissions), {
      secure: true,
    });
  }, [user]);

  return <div />;
};

const Component = createFragmentContainer(AppSideEffects, {
  user: graphql`
    fragment AppSideEffects_user on User {
      userId
      email
      shoppingWithCeoUser {
        customUrl
        trinityDealerId
      }
      permissions {
        id
        oneCommerce {
          id
          ceo {
            access
          }
          admin {
            access
          }
          superAdmin {
            access
          }
          preferredCustomer {
            access
          }
          subscriber {
            access
          }
        }
      }
    }
  `,
});

const noVariables = {};
// eslint-disable-next-line react/display-name
export default ({
  query,
  environment,
}: {
  query: any | null;
  environment: Environment;
}) => {
  return (
    <QueryRenderer
      dataFrom="STORE_THEN_NETWORK"
      variables={noVariables}
      environment={environment}
      query={graphql`
        query AppSideEffectsQuery {
          viewer {
            id
            user {
              id
              ...AppSideEffects_user
            }
          }
        }
      `}
      render={({ error, props: propsFromRender }: any) => {
        if (error) {
          return <div />;
        }
        if (
          propsFromRender &&
          propsFromRender.viewer &&
          propsFromRender.viewer.user
        ) {
          return <Component query={query} user={propsFromRender.viewer.user} />;
        }

        return null;
      }}
    />
  );
};
